if (!customElements.get('sticky-cart-button')) {
  customElements.define('sticky-cart-button', class StickyCartButton extends HTMLElement {
    constructor() {
      super();
      this.targetId = this.dataset.target;
      this.action = this.dataset.action || 'add_to_cart';
      this.targetEl = null;
      this.stickyButton = null;
      this.resizeTimeout = null;
      this.observer = null;
      this.targetObserver = null;
      this.onVariantChangeUnsubscriber = null;
    }

    connectedCallback() {
      this.targetEl = document.getElementById(this.targetId);
      if (!this.targetEl) return;
      this.stickyButton = this.querySelector('button');

      this.updateVisibility = this.updateVisibility.bind(this);
      this.handleWindowResize = this.handleWindowResize.bind(this);
      this.handleClick = this.handleClick.bind(this);

      this.observeTarget();
      this.subscribeToVariantChange();
      this.syncButtonState();
      this.updateVisibility();
      this.observeFooter();

      window.addEventListener('scroll', this.updateVisibility, { passive: true });
      window.addEventListener('resize', this.handleWindowResize);
      this.addEventListener('click', this.handleClick);
    }

    observeFooter() {
      const footer = document.querySelector('footer');
      if (!footer) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.classList.add('footer-visible');
          } else {
            this.classList.remove('footer-visible');
          }
        });
      }, {
        rootMargin: '0px',
        threshold: 0
      });

      this.observer.observe(footer);
    }

    observeTarget() {
      if (!this.targetEl) return;

      this.targetObserver = new MutationObserver(() => {
        this.syncButtonState();
        this.updateVisibility();
      });

      this.targetObserver.observe(this.targetEl, {
        attributes: true,
        attributeFilter: ['disabled', 'class', 'aria-disabled']
      });
    }

    subscribeToVariantChange() {
      if (typeof subscribe !== 'function' || !PUB_SUB_EVENTS?.variantChange) return;

      this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
        this.syncButtonState();
        this.updateVisibility();
      });
    }

    syncButtonState() {
      if (!this.targetEl || !this.stickyButton) return;

      this.stickyButton.innerHTML = this.targetEl.innerHTML;
      this.stickyButton.className = this.targetEl.className;
      this.stickyButton.removeAttribute('id');
      this.stickyButton.removeAttribute('name');
      this.stickyButton.removeAttribute('type');
      this.stickyButton.removeAttribute('aria-label');
      this.stickyButton.removeAttribute('aria-disabled');
    }

    updateVisibility() {
      if (!this.targetEl) return;

      const rect = this.targetEl.getBoundingClientRect();
      const isTargetLoading = this.targetEl.classList.contains('loading');
      const isTargetEnabled = !this.targetEl.hasAttribute('disabled') && this.targetEl.getAttribute('aria-disabled') !== 'true';
      const shouldShow = isTargetEnabled || isTargetLoading;
      
      if (shouldShow && rect.bottom < -20 && !this.classList.contains('footer-visible')) {
        this.classList.add("visible");
      } else {
        this.classList.remove("visible");
      }
    }

    handleWindowResize() {
      if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(this.updateVisibility, 50);
    }

    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.targetEl) return;

      event.preventDefault();

      if (this.action === 'scroll_to_form') {
        const offset = window.innerHeight / 2;
        const targetPosition = this.targetEl.offsetTop - offset;
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
        return;
      }

      const isTargetLoading = this.targetEl.classList.contains('loading');
      const isTargetEnabled = !this.targetEl.hasAttribute('disabled') && this.targetEl.getAttribute('aria-disabled') !== 'true';
      if (!isTargetEnabled || isTargetLoading) return;

      this.stickyButton?.classList.add('loading');
      this.stickyButton?.querySelector('.loading-spinner')?.classList.remove('hidden');

      this.targetEl.dataset.stickySubmit = 'true';
      this.targetEl.click();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.updateVisibility);
      window.removeEventListener('resize', this.handleWindowResize);
      this.removeEventListener('click', this.handleClick);

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.targetObserver) {
        this.targetObserver.disconnect();
        this.targetObserver = null;
      }
      this.onVariantChangeUnsubscriber?.();
      this.onVariantChangeUnsubscriber = null;

      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = null;
      }
    }
  });
}
